import type { ReactNode } from 'react';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor, type RootState } from './store';
import { CurrencyProvider } from '@kss/currency';
import { ThemeSync } from '@kss/theme';
import { ToastProvider, CartProvider, OrderSessionProvider, SearchProvider } from '@kss/ui';
import { ConfigBootstrap } from './ConfigBootstrap';

/**
 * The per-customer scope key. We use the PHONE, not `user.id`: the OTP backend
 * returns a constant id (1) for every customer, so id-scoping would share one
 * cart/order-session across all users. Phone is unique → true per-customer isolation.
 */
function useCustomerKey(): string | null {
  return useSelector((s: RootState) => s.auth.user?.phone ?? s.auth.user?.id?.toString() ?? null);
}

/** Scopes the cart to the signed-in customer so carts don't leak across sessions. */
function ScopedCart({ children }: { children: ReactNode }) {
  return <CartProvider userId={useCustomerKey()}>{children}</CartProvider>;
}

/** Scopes the running orders + bill to the signed-in customer (fixes cross-customer leak). */
function ScopedOrderSession({ children }: { children: ReactNode }) {
  return <OrderSessionProvider userId={useCustomerKey()}>{children}</OrderSessionProvider>;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ToastProvider>
          <SearchProvider>
            <ScopedCart>
              <ScopedOrderSession>
                <CurrencyProvider>
                  <ThemeSync />
                  <ConfigBootstrap />
                  {children}
                </CurrencyProvider>
              </ScopedOrderSession>
            </ScopedCart>
          </SearchProvider>
        </ToastProvider>
      </PersistGate>
    </Provider>
  );
}
